import { memo, useCallback, useEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ToolClassification } from "../../types/message";
import "../../Styles/Message/ResearchActivityDisplay.css";
import {
    ArrowRight01Icon,
    BubbleChatIcon,
    CheckListIcon,
    CircleIcon,
    InternetIcon,
    Search02Icon,
    ViewIcon,
} from "hugeicons-react";
import { useResourceContext } from "../../contexts/ResourceContext";
import { useWindow } from "../../contexts/WindowContext";
import { CloseButton } from "../Layout/Buttons/CloseButton";
import {
    askedLabel,
    getLiveResearchLabel,
    pendingQuestion,
    type ResearchActivityItem,
} from "../../handler/TextRenderer/researchActivity";
import { useAskUser } from "../../contexts/AskUserContext";
import type { AskUserCardData } from "../../helpers/askUser";
import { AnimatedStatusText } from "./AnimatedStatusText";
import { MessageLoadingIndicator, MessageLoadingState } from "./MessageLoadingState";

const getToolTypeIcon = (type: ToolClassification, size = 18) => {
    switch (type) {
        case "vision":
            return <ViewIcon size={size} className="tool-type-icon" />;
        case "web":
            return <InternetIcon size={size} className="tool-type-icon" />;
        case "search":
            return <Search02Icon size={size} className="tool-type-icon" />;
        case "space":
            return <BubbleChatIcon size={size} className="tool-type-icon" />;
        case "quiz":
            return <CheckListIcon size={size} className="tool-type-icon" />;
        default:
            return null;
    }
};

const getHost = (urlString: string) => {
    try {
        return new URL(urlString).hostname;
    } catch {
        return null;
    }
};

const shortUrl = (urlString: string) => getHost(urlString) ?? urlString;

const displayHost = (urlString: string) => {
    const host = getHost(urlString);
    return host ? host.replace(/^www\./, "") : null;
};

const faviconUrl = (urlString: string, size = 128) => {
    const host = getHost(urlString);
    return host
        ? `https://www.google.com/s2/favicons?domain=${host}&sz=${size}`
        : null;
};

const openUrl = (urlString: string) =>
    window.open(urlString, "_blank", "noopener,noreferrer");

const uniqueStrings = (values: string[]) => Array.from(new Set(values));

const toolTitle = (item: ResearchActivityItem) => {
    const oneFile = item.files.length === 1;
    if (item.isFailed) {
        switch (item.kind) {
            case "search": return "Search Failed";
            case "web": return "Couldn't Open Sources";
            case "vision": return "Couldn't Review Documents";
            case "space": return "Couldn't Read Space Chats";
            case "quiz": return "Couldn't Make a Quiz";
            default: return "Research Tool Failed";
        }
    }
    if (!item.isComplete) {
        switch (item.kind) {
            case "search": return "Searching the Web";
            case "web": return "Browsing Sources";
            case "vision": return oneFile ? "Reviewing a Document" : "Reviewing Documents";
            case "space": return "Reading Space Chats";
            case "quiz": return "Making a Quiz";
            default: return "Using a Research Tool";
        }
    }
    switch (item.kind) {
        case "search": return "Searched the Web";
        case "web": return "Browsed Sources";
        case "vision": return oneFile ? "Reviewed a Document" : "Reviewed Documents";
        case "space": return "Read Space Chats";
        case "quiz": return "Made a Quiz";
        default: return "Used a Research Tool";
    }
};

const activityTitle = (item: ResearchActivityItem) => {
    switch (item.kind) {
        case "thinking":
            return "Thought Process";
        case "reasoning":
            return item.title || "Reasoning";
        case "narration":
            return "Thinking Step";
        case "question":
            switch (item.question?.status) {
                case "answered": return "You Answered";
                case "skipped": return "Question Skipped";
                default: return askedLabel(item.question?.questions.length ?? 1, "Asked You");
            }
        default:
            return toolTitle(item);
    }
};

const activityIcon = (item: ResearchActivityItem) => {
    if (item.kind === "thinking" || item.kind === "reasoning" || item.kind === "narration" || item.kind === "question") {
        return <CircleIcon size={8} fill="currentColor" />;
    }
    return getToolTypeIcon(item.kind, 16) || <CircleIcon size={8} fill="currentColor" />;
};

/** The user's reply to question `index`, as shown once the card is settled. */
const answerSummary = (card: AskUserCardData, index: number): string => {
    if (card.status === "pending") return "Waiting for your answer";
    const answer = card.answers?.[index];
    const parts = card.status === "answered" && answer ? [...answer.selected] : [];
    if (card.status === "answered" && answer?.other?.trim()) parts.push(`\u201C${answer.other.trim()}\u201D`);
    if (parts.length > 0) return parts.join(", ");
    return card.status === "skipped" ? "Skipped" : "No answer";
};

export const ResearchActivityDisplay = ({
    items: rawItems,
    messageId,
    isActive = false,
}: {
    items: ResearchActivityItem[];
    messageId: string;
    isActive?: boolean;
}) => {
    // A block still running in a finished run was cut off with its stream.
    const items = isActive || rawItems.every(item => item.isComplete)
        ? rawItems
        : rawItems.map(item => item.isComplete ? item : { ...item, isComplete: true });
    const { handleResourceClick } = useResourceContext();
    const [showActivitySheet, setShowActivitySheet] = useState(false);
    const closeActivitySheet = useCallback(() => setShowActivitySheet(false), []);
    const openActivitySheet = useCallback(() => setShowActivitySheet(true), []);
    const openActivityFile = useCallback((file: string) => {
        handleResourceClick(file, messageId);
        setShowActivitySheet(false);
    }, [handleResourceClick, messageId]);
    const urls = uniqueStrings(items.flatMap(item => item.urls));
    const queries = uniqueStrings(items.flatMap(item => item.queries));
    const files = uniqueStrings(items.flatMap(item => item.files));
    const hasItems = items.length > 0;
    const hasToolActivity = items.some(item =>
        item.kind === "search" || item.kind === "web" || item.kind === "vision"
    );
    const liveLabel = isActive ? getLiveResearchLabel(items) : null;
    const isShowingThoughtContent = Boolean(liveLabel?.isThought);
    // Waiting on the user, not working: no loading animation.
    const waitingQuestion = isActive ? pendingQuestion(items) : null;
    const askUser = useAskUser();
    // The live question is answered in place of the input; once skipped
    // there, the activity offers to bring it back.
    const canReopenQuestion = Boolean(
        waitingQuestion
        && askUser?.activeQuestion?.card.id === waitingQuestion.id
        && !askUser.isComposerOpen
    );
    const questionCount = items.reduce(
        (count, item) => count + (item.kind === "question" ? item.question?.questions.length ?? 0 : 0),
        0
    );

    const label = isActive
        ? (liveLabel?.text || (hasToolActivity ? "Researching" : "Thinking"))
        : urls.length > 0
            ? (urls.length === 1 ? "Researched" : `Researched ${urls.length} Sources`)
            : queries.length > 0
                ? "Searched the Web"
                : files.length > 0
                    ? `Reviewed ${files.length} ${files.length === 1 ? "Document" : "Documents"}`
                    : questionCount > 0
                        ? askedLabel(questionCount, "Asked You")
                        : "Read Thoughts";

    if (!hasItems && !isActive) return null;

    return (
        <div className={`tool-call-display research-activity-display${isActive ? " is-active" : ""}`}>
            <div className="tool-call-reveal">
                <button
                    type="button"
                    className={`tool-call-trigger research-activity-trigger${isShowingThoughtContent ? " has-thought-content" : ""}${!hasItems ? " is-loading-only" : ""}`}
                    onClick={hasItems ? openActivitySheet : undefined}
                    aria-label={hasItems ? `${label}. Read Thoughts` : label}
                    aria-busy={isActive && !waitingQuestion}
                >
                    {isActive && !waitingQuestion ? (
                        <MessageLoadingState label={label} inline />
                    ) : (
                        <>
                            {urls.length > 0 ? (
                                <div className="web-tool-favicons">
                                    {urls.slice(0, 4).map((url, idx) => (
                                        <span
                                            key={url}
                                            className="favicon-reveal-slot"
                                            style={{
                                                "--favicon-slot-start": idx === 0 ? "18px" : "0px",
                                                "--favicon-slot-width": idx === 0 ? "24px" : "12px",
                                                "--favicon-offset": idx === 0 ? "0px" : "-12px",
                                                "--favicon-delay": `${idx * 55}ms`,
                                                "--favicon-index": idx + 1,
                                            } as CSSProperties}
                                        >
                                            <FaviconCircle urlString={url} />
                                        </span>
                                    ))}
                                </div>
                            ) : hasToolActivity ? (
                                <Search02Icon size={18} className="tool-type-icon" />
                            ) : (
                                <CircleIcon size={9} fill="currentColor" className="tool-type-icon" />
                            )}
                            <AnimatedStatusText
                                text={label}
                                className={`tool-call-trigger-label${isShowingThoughtContent ? " is-thought-content" : ""}`}
                            />
                            <ArrowRight01Icon size={16} className="tool-call-chevron" />
                        </>
                    )}
                </button>
                {canReopenQuestion && (
                    <button type="button" className="ask-user-card__link" onClick={askUser?.reopen}>
                        Answer
                    </button>
                )}
            </div>

            {hasItems && showActivitySheet &&
                createPortal(
                    <ResearchActivitySheet
                        items={items}
                        isActive={isActive}
                        hasToolActivity={hasToolActivity}
                        onOpenFile={openActivityFile}
                        onClose={closeActivitySheet}
                    />,
                    document.body
                )}
        </div>
    );
};

const FaviconCircle = ({ urlString }: { urlString: string }) => {
    const [failed, setFailed] = useState(false);
    const host = displayHost(urlString);
    const src = faviconUrl(urlString, 64);
    const initial = (host ?? "?").charAt(0).toUpperCase();

    return (
        <div className="favicon-circle">
            {src && !failed ? (
                <img
                    src={src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={() => setFailed(true)}
                    className="favicon-circle-img"
                />
            ) : (
                <span className="favicon-placeholder">{initial}</span>
            )}
        </div>
    );
};

const FaviconSquare = ({ urlString }: { urlString: string }) => {
    const [failed, setFailed] = useState(false);
    const host = displayHost(urlString);
    const src = faviconUrl(urlString, 128);
    const initial = (host ?? "?").charAt(0).toUpperCase();

    return (
        <div className="favicon-square">
            {src && !failed ? (
                <img
                    src={src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={() => setFailed(true)}
                    className="favicon-square-img"
                />
            ) : (
                <span className="favicon-placeholder">{initial}</span>
            )}
        </div>
    );
};

const ToolSheet = ({
    title,
    onClose,
    children,
}: {
    title: string;
    onClose: () => void;
    children: React.ReactNode;
}) => {
    const { windowWidth } = useWindow();
    const isForcedMobilePreview = process.env.NODE_ENV === "development"
        && window.location.pathname === "/dev/research-activity"
        && new URLSearchParams(window.location.search).get("viewport") === "mobile";
    const isMobile = windowWidth < 768 || isForcedMobilePreview;

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", handleEscape);
        };
    }, [onClose]);

    return (
        <div
            className={`tool-sheet-overlay ${isMobile ? "is-mobile" : "is-desktop"}`}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
            role="dialog"
            aria-modal="true"
            aria-label={title}
        >
            <div className="tool-sheet-panel">
                {isMobile && <div className="tool-sheet-grabber" />}
                <div className="tool-sheet-header">
                    <span className="tool-sheet-title">{title}</span>
                    <CloseButton onClick={onClose} />
                </div>
                <div className="tool-sheet-body">{children}</div>
            </div>
        </div>
    );
};

const sameStringArray = (first: string[], second: string[]) => (
    first.length === second.length && first.every((value, index) => value === second[index])
);

const ResearchActivityTimelineItem = memo(({
    item,
    index,
    onOpenFile,
}: {
    item: ResearchActivityItem;
    index: number;
    onOpenFile: (file: string) => void;
}) => (
    <li
        className={`research-activity-item${!item.isComplete ? " is-active" : ""}`}
    >
        <div className="research-activity-marker" aria-hidden="true">
            {item.isComplete ? activityIcon(item) : <MessageLoadingIndicator />}
        </div>
        <div className="research-activity-card">
            <div className="research-activity-card-header">
                <span className="research-activity-card-title">
                    {activityTitle(item)}
                </span>
            </div>

            {item.content && (
                <div className="research-activity-thought">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {item.content}
                    </ReactMarkdown>
                </div>
            )}

            {item.question && (
                <dl className="ask-user-card__summary">
                    {item.question.questions.map((question, questionIndex) => (
                        <div key={questionIndex}>
                            <dt>{question.question}</dt>
                            <dd>{answerSummary(item.question!, questionIndex)}</dd>
                        </div>
                    ))}
                </dl>
            )}

            {item.queries.length > 0 && (
                <ol className="research-query-list">
                    {item.queries.map((query, queryIndex) => (
                        <li key={`${query}-${queryIndex}`} className="research-query-item">
                            <span className="search-thoughts-index">{queryIndex + 1}</span>
                            <span className="search-thoughts-text">{query}</span>
                        </li>
                    ))}
                </ol>
            )}

            {item.urls.length > 0 && (
                <div className="research-source-grid">
                    {item.urls.map((url, urlIndex) => {
                        const host = displayHost(url);
                        return (
                            <button
                                key={`${url}-${urlIndex}`}
                                type="button"
                                className="research-source-card"
                                onClick={() => openUrl(url)}
                                aria-label={`Open ${host ?? shortUrl(url)}`}
                            >
                                <FaviconSquare urlString={url} />
                                <span className="research-source-name">
                                    {host ?? shortUrl(url)}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {item.files.length > 0 && (
                <div className="research-file-list">
                    {item.files.map((file, fileIndex) => (
                        <button
                            key={`${file}-${fileIndex}`}
                            type="button"
                            className="research-file-item"
                            onClick={() => onOpenFile(file)}
                            aria-label={`Open ${file}`}
                        >
                            {item.kind !== "thinking"
                                && item.kind !== "reasoning"
                                && item.kind !== "narration"
                                && item.kind !== "question"
                                ? getToolTypeIcon(item.kind, 16)
                                : null}
                            <span className="tool-file-name">{file}</span>
                            <ArrowRight01Icon size={14} className="tool-call-chevron" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    </li>
), (previous, next) => (
    previous.index === next.index
    && previous.onOpenFile === next.onOpenFile
    && previous.item.kind === next.item.kind
    && previous.item.title === next.item.title
    && previous.item.content === next.item.content
    && previous.item.isComplete === next.item.isComplete
    && previous.item.question?.status === next.item.question?.status
    && sameStringArray(previous.item.queries, next.item.queries)
    && sameStringArray(previous.item.urls, next.item.urls)
    && sameStringArray(previous.item.files, next.item.files)
));
ResearchActivityTimelineItem.displayName = "ResearchActivityTimelineItem";

const ResearchActivitySheet = ({
    items,
    isActive,
    hasToolActivity,
    onOpenFile,
    onClose,
}: {
    items: ResearchActivityItem[];
    isActive: boolean;
    hasToolActivity: boolean;
    onOpenFile: (file: string) => void;
    onClose: () => void;
}) => (
    <ToolSheet
        title={isActive ? (hasToolActivity ? "Researching" : "Thinking") : "Thoughts"}
        onClose={onClose}
    >
        <ol className="research-activity-timeline">
            {items.map((item, index) => (
                <ResearchActivityTimelineItem
                    key={`${item.kind}-${index}`}
                    item={item}
                    index={index}
                    onOpenFile={onOpenFile}
                />
            ))}
        </ol>

    </ToolSheet>
);
